import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileText, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  legal_name: "",
  company_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  tax_id: "",
  tax_id_type: "",
};

const TAX_ID_TYPES = [
  { value: "none", label: "None" },
  { value: "gst", label: "GST (India)" },
  { value: "vat", label: "VAT (EU/UK)" },
  { value: "ein", label: "EIN (US)" },
  { value: "other", label: "Other" },
];

function money(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(amount);
}

export default function BillingSettings() {
  const { token } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const nextPath = params.get("next");
  const reason = params.get("reason");
  const isPaymentGate = reason === "payment";

  const [form, setForm] = useState(EMPTY);
  const [savedProfile, setSavedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [regenerating, setRegenerating] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [{ data: profileRes }, { data: invoiceRes }] = await Promise.all([
          http.get("/billing/profile"),
          http.get("/billing/invoices"),
        ]);
        if (cancelled) return;
        if (profileRes.profile) {
          const p = profileRes.profile;
          setForm({
            legal_name: p.legal_name || "",
            company_name: p.company_name || "",
            address_line1: p.address_line1 || "",
            address_line2: p.address_line2 || "",
            city: p.city || "",
            state: p.state || "",
            postal_code: p.postal_code || "",
            country: p.country || "",
            tax_id: p.tax_id || "",
            tax_id_type: p.tax_id_type || "",
          });
          setSavedProfile(p);
        }
        setInvoices(invoiceRes.invoices || []);
      } catch (err) {
        toast.error("Could not load billing details");
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
          setInvoicesLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    if (!form.legal_name || !form.address_line1 || !form.city || !form.postal_code || !form.country) {
      toast.error("Please fill name, address, city, postal code and country.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Normalise: empty strings → null for optional fields backend treats as optional
      if (!payload.tax_id_type || payload.tax_id_type === "none") {
        payload.tax_id_type = null;
        payload.tax_id = null;
      }
      if (!payload.tax_id) payload.tax_id = null;
      if (!payload.company_name) payload.company_name = null;
      if (!payload.address_line2) payload.address_line2 = null;
      if (!payload.state) payload.state = null;
      const { data } = await http.put("/billing/profile", payload);
      setSavedProfile(data.profile);
      toast.success("Billing details saved");
      // Payment gate: resume the purchase page the user was on before redirect.
      if (isPaymentGate && nextPath) {
        setTimeout(() => navigate(nextPath), 400);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  async function downloadPdf(inv) {
    setDownloading(inv.id);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      toast.error("Could not download invoice");
    } finally {
      setDownloading(null);
    }
  }

  async function regenerateInvoice(inv) {
    // Guard: at minimum the required fields must be present.
    const missing = ["legal_name", "address_line1", "city", "postal_code", "country"].filter(
      (k) => !(form[k] || "").trim()
    );
    if (missing.length) {
      toast.error("Fill in the required billing fields first, then regenerate.");
      return;
    }
    // If the form has unsaved changes, remind the user to save first.
    const dirty = savedProfile
      ? Object.keys(EMPTY).some((k) => (form[k] || "") !== (savedProfile[k] || ""))
      : true;
    if (dirty) {
      toast.error("Save your billing details first, then click Regenerate.");
      return;
    }
    setRegenerating(inv.id);
    try {
      const { data } = await http.post(`/billing/invoices/${inv.id}/regenerate`);
      // swap the updated invoice into the list so the UI reflects the refresh
      setInvoices((prev) => prev.map((x) => (x.id === inv.id ? { ...x, ...data.invoice } : x)));
      toast.success(`Invoice ${inv.invoice_number} refreshed with new billing details.`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Could not regenerate");
    } finally {
      setRegenerating(null);
    }
  }

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="billing-settings-page">
      <Link
        to="/dashboard"
        className="text-[10px] uppercase tracking-widestx font-semibold text-ink-400 hover:text-ink-900 inline-flex items-center gap-1 mb-6"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to dashboard
      </Link>

      <header className="mb-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-ink-900 leading-tight">
          Billing & invoices
        </h1>
        <p className="text-sm text-ink-500 mt-2 max-w-2xl">
          These details appear on every invoice as the <strong>Bill To</strong> party.
          Indian customers with a GSTIN automatically get an 18% IGST line added to invoices.
        </p>
      </header>

      {/* Payment-gate banner — shown when interceptor redirected mid-purchase */}
      {isPaymentGate && (
        <div
          data-testid="billing-payment-gate-banner"
          className="mb-8 flex items-start gap-3 rounded-sm border border-[#F57F17] bg-[#FFF8E1] px-4 py-3"
        >
          <AlertTriangle className="w-4 h-4 text-[#F57F17] mt-0.5 shrink-0" />
          <div className="text-[13px] text-[#7B5A00] leading-relaxed">
            <div className="font-semibold text-ink-900">Complete your billing details before making a payment.</div>
            This information is required on your GST/tax invoice.
            {nextPath && (
              <> After saving, we'll take you back to <span className="font-mono">{nextPath}</span>.</>
            )}
          </div>
        </div>
      )}

      {/* ─── Billing details ─── */}
      <Card className="rounded-sm border-ink-100 mb-12" data-testid="billing-profile-card">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-extrabold text-lg text-ink-900">Billing details</h2>
            {savedProfile && (
              <span className="text-[10px] uppercase tracking-widestx font-semibold text-[#2E7D32] inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Saved
              </span>
            )}
          </div>

          {profileLoading ? (
            <div className="py-6 text-sm text-ink-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Legal name *">
                <Input
                  data-testid="bp-legal-name"
                  value={form.legal_name}
                  onChange={(e) => set("legal_name", e.target.value)}
                  placeholder="Jane Cooper"
                />
              </Field>
              <Field label="Company name">
                <Input
                  data-testid="bp-company-name"
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  placeholder="Acme Inc."
                />
              </Field>
              <Field label="Address line 1 *" className="sm:col-span-2">
                <Input
                  data-testid="bp-address1"
                  value={form.address_line1}
                  onChange={(e) => set("address_line1", e.target.value)}
                  placeholder="123 Main St"
                />
              </Field>
              <Field label="Address line 2" className="sm:col-span-2">
                <Input
                  data-testid="bp-address2"
                  value={form.address_line2}
                  onChange={(e) => set("address_line2", e.target.value)}
                  placeholder="Suite 400"
                />
              </Field>
              <Field label="City *">
                <Input
                  data-testid="bp-city"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="Austin"
                />
              </Field>
              <Field label="State / Region">
                <Input
                  data-testid="bp-state"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  placeholder="TX"
                />
              </Field>
              <Field label="Postal code *">
                <Input
                  data-testid="bp-postal"
                  value={form.postal_code}
                  onChange={(e) => set("postal_code", e.target.value)}
                  placeholder="73301"
                />
              </Field>
              <Field label="Country *">
                <Input
                  data-testid="bp-country"
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  placeholder="US (or India, United Kingdom, etc.)"
                />
              </Field>
              <Field label="Tax ID type">
                <Select
                  value={form.tax_id_type || "none"}
                  onValueChange={(v) => set("tax_id_type", v === "none" ? "" : v)}
                >
                  <SelectTrigger data-testid="bp-tax-type" className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_ID_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tax ID">
                <Input
                  data-testid="bp-tax-id"
                  value={form.tax_id}
                  onChange={(e) => set("tax_id", e.target.value)}
                  placeholder="29ABCDE1234F1Z5"
                  disabled={!form.tax_id_type || form.tax_id_type === "none"}
                />
              </Field>
              <div className="sm:col-span-2 flex items-center justify-end gap-3 mt-4 pt-4 border-t border-ink-100">
                <Button
                  data-testid="bp-save"
                  onClick={save}
                  disabled={saving}
                  className="rounded-sm bg-ink-900 hover:bg-ink-700 text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save billing details"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ─── Invoices ─── */}
      <Card className="rounded-sm border-ink-100" data-testid="billing-invoices-card">
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h2 className="font-display font-extrabold text-lg text-ink-900">Invoices</h2>
              <p className="text-[12px] text-ink-500 mt-1 max-w-xl leading-relaxed">
                Bill-To details are frozen when an invoice is issued. If you paid before
                filling in your billing profile, update the fields above, save, and click
                <span className="font-mono text-ink-700"> Regenerate</span> next to any invoice
                to refresh its Bill-To — invoice number and totals stay the same.
              </p>
            </div>
          </div>
          {invoicesLoading ? (
            <div className="py-6 text-sm text-ink-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : invoices.length === 0 ? (
            <div
              data-testid="billing-no-invoices"
              className="text-sm text-ink-500 py-8 text-center border border-dashed border-ink-200 rounded-sm"
            >
              <FileText className="w-6 h-6 mx-auto text-ink-300 mb-2" />
              No invoices yet. Invoices are generated automatically after each successful payment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="billing-invoices-table">
                <thead>
                  <tr className="border-b border-ink-100 text-[10px] uppercase tracking-widestx text-ink-400">
                    <th className="text-left font-semibold py-2 pr-3">Invoice</th>
                    <th className="text-left font-semibold py-2 pr-3">Date</th>
                    <th className="text-left font-semibold py-2 pr-3">Description</th>
                    <th className="text-right font-semibold py-2 pr-3">Amount</th>
                    <th className="text-left font-semibold py-2 pr-3">Status</th>
                    <th className="text-right font-semibold py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className="border-b border-ink-50"
                      data-testid={`invoice-row-${inv.invoice_number}`}
                    >
                      <td className="py-3 pr-3 font-mono text-xs text-ink-900">{inv.invoice_number}</td>
                      <td className="py-3 pr-3 text-ink-700">{(inv.issued_at || "").slice(0, 10)}</td>
                      <td className="py-3 pr-3 text-ink-700 truncate max-w-[260px]">
                        {inv.line_items?.[0]?.description || "—"}
                      </td>
                      <td className="py-3 pr-3 text-right font-medium text-ink-900">
                        {money(inv.total, inv.currency)}
                      </td>
                      <td className="py-3 pr-3">
                        <span
                          className={`inline-block text-[10px] uppercase tracking-widestx font-semibold px-2 py-0.5 rounded-sm ${
                            inv.status === "paid"
                              ? "bg-[#E8F5E9] text-[#2E7D32]"
                              : "bg-ink-100 text-ink-700"
                          }`}
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => regenerateInvoice(inv)}
                            disabled={regenerating === inv.id || downloading === inv.id}
                            className="rounded-sm h-8 text-xs"
                            data-testid={`invoice-regenerate-${inv.invoice_number}`}
                            title="Refresh Bill-To with your current billing details"
                          >
                            {regenerating === inv.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Regenerate
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadPdf(inv)}
                            disabled={downloading === inv.id || regenerating === inv.id}
                            className="rounded-sm h-8 text-xs"
                            data-testid={`invoice-download-${inv.invoice_number}`}
                          >
                            {downloading === inv.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-3 h-3 mr-1" />
                                PDF
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </main>
  );
}

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500 mb-1.5 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
